import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ClientPortalAuthProvider } from "@/contexts/ClientPortalAuthContext";
import { Toaster } from "@/components/ui/sonner";

import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import ProjectDetail from "@/pages/ProjectDetail";
import MyTasks from "@/pages/MyTasks";
import Staff from "@/pages/Staff";
import LeavePage from "@/pages/Leave";
import Holidays from "@/pages/Holidays";
import Resources from "@/pages/Resources";
import Analytics from "@/pages/Analytics";
import AuditLog from "@/pages/AuditLog";
import Attendance from "@/pages/Attendance";
import MyAttendance from "@/pages/MyAttendance";
import SettingsPage from "@/pages/Settings";
import RolesPermissions from "@/pages/RolesPermissions";
import Knowledge from "@/pages/Knowledge";
import SecondBrain from "@/pages/secondBrain/SecondBrain";
import RecentlyDeleted from "@/pages/RecentlyDeleted";
import FormsList from "@/pages/forms/FormsList";
import TaskTemplates from "@/pages/TaskTemplates";
import FormBuilder from "@/pages/forms/FormBuilder";
import FormSubmissions from "@/pages/forms/FormSubmissions";
import PublicForm from "@/pages/forms/PublicForm";

// Client Portal (isolated)
import CpLogin from "@/pages/clientPortal/CpLogin";
import CpLayout from "@/pages/clientPortal/CpLayout";
import CpDashboard from "@/pages/clientPortal/CpDashboard";
import CpSection from "@/pages/clientPortal/CpSection";
import CpCompliance from "@/pages/clientPortal/CpCompliance";
import CpRfis from "@/pages/clientPortal/CpRfis";
import CpRfiDetail from "@/pages/clientPortal/CpRfiDetail";
import CpChangePassword from "@/pages/clientPortal/CpChangePassword";
import CpForgotPassword from "@/pages/clientPortal/CpForgotPassword";
import CpResetPassword from "@/pages/clientPortal/CpResetPassword";

// Role constants — stable references so ProtectedRoute doesn't see a new array on every render
const ROLES_ADMIN = ["Admin"];
const ROLES_ADMIN_MANAGER = ["Admin", "Manager"];

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-[#0a2540] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // CRITICAL: detect session_id in hash synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Public form page — no auth, no Layout. Lives at /f/:token.
  if (location.pathname.startsWith("/f/")) {
    return (
      <Routes>
        <Route path="/f/:token" element={<PublicForm />} />
      </Routes>
    );
  }
  // Client Portal: isolated namespace, own auth context, NO Griffin staff layout
  if (location.pathname.startsWith("/client-portal")) {
    return (
      <ClientPortalAuthProvider>
        <Routes>
          <Route path="/client-portal/login" element={<CpLogin />} />
          <Route path="/client-portal/forgot-password" element={<CpForgotPassword />} />
          <Route path="/client-portal/reset-password/:token" element={<CpResetPassword />} />
          <Route path="/client-portal" element={<CpLayout />}>
            <Route index element={<Navigate to="/client-portal/dashboard" replace />} />
            <Route path="dashboard" element={<CpDashboard />} />
            <Route path="sections/:section" element={<CpSection />} />
            <Route path="compliance" element={<CpCompliance />} />
            <Route path="queries" element={<CpRfis />} />
            <Route path="queries/:id" element={<CpRfiDetail />} />
            <Route path="change-password" element={<CpChangePassword />} />
          </Route>
          <Route path="*" element={<Navigate to="/client-portal/dashboard" replace />} />
        </Routes>
      </ClientPortalAuthProvider>
    );
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/my-tasks" element={<MyTasks />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:id" element={<ClientDetail />} />
        <Route path="/knowledge" element={<Knowledge />} />
        <Route path="/second-brain" element={<SecondBrain />} />
        <Route path="/recently-deleted" element={<RecentlyDeleted />} />
        <Route path="/forms" element={<FormsList />} />
        <Route path="/task-templates" element={
          <ProtectedRoute roles={ROLES_ADMIN_MANAGER}><TaskTemplates /></ProtectedRoute>
        } />
        <Route path="/forms/:id/edit" element={
          <ProtectedRoute roles={ROLES_ADMIN_MANAGER}><FormBuilder /></ProtectedRoute>
        } />
        <Route path="/forms/:id/submissions" element={<FormSubmissions />} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/staff" element={
          <ProtectedRoute roles={ROLES_ADMIN_MANAGER}><Staff /></ProtectedRoute>
        } />
        <Route path="/leave" element={<LeavePage />} />
        <Route path="/holidays" element={<Holidays />} />
        <Route path="/attendance" element={
          <ProtectedRoute roles={ROLES_ADMIN_MANAGER}><Attendance /></ProtectedRoute>
        } />
        <Route path="/my-attendance" element={<MyAttendance />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/analytics" element={
          <ProtectedRoute roles={ROLES_ADMIN_MANAGER}><Analytics /></ProtectedRoute>
        } />
        <Route path="/activity" element={
          <ProtectedRoute roles={ROLES_ADMIN}><AuditLog /></ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute roles={ROLES_ADMIN}><SettingsPage /></ProtectedRoute>
        } />
        <Route path="/roles" element={
          <ProtectedRoute roles={ROLES_ADMIN}><RolesPermissions /></ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
